import { useState } from 'react';
import { api, ApiError, num } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../../lib/roles';
import type {
  Bid,
  EvaluationCriterion,
  EvaluationPanelMember,
  EvaluationReport,
  PanelAssignResponse,
  TenderNotice,
} from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { ErrorNote, Field, InfoNote, Spinner, StatusBadge } from '../../../ui/components';

const ALL_M11_STAFF = Object.values(M11_ROLES);

export function EvaluationTab({ tender, onChanged }: { tender: TenderNotice; onChanged: () => void }) {
  const { persona } = useAuth();
  const isStaff = hasAnyRole(persona?.roles, ...ALL_M11_STAFF);

  if (!isStaff) {
    return (
      <InfoNote message="Evaluation committee actions require a Public Procurement staff role (Procurement Officer, Evaluator, Tender Board, or NPTB Oversight) — sign in as one from the sidebar." />
    );
  }

  const isOfficerOrBoard = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER, M11_ROLES.TENDER_BOARD);
  const isEvaluator = hasAnyRole(persona?.roles, M11_ROLES.EVALUATOR);
  const isBoard = hasAnyRole(persona?.roles, M11_ROLES.TENDER_BOARD);
  const canViewReport = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER, M11_ROLES.TENDER_BOARD, M11_ROLES.NPTB_OVERSIGHT);

  const panelQ = useApiQuery<{ items: EvaluationPanelMember[] }>(`/api/v1/m11/tenders/${tender.id}/panel`);
  const myMembership = panelQ.data?.items.find((p) => p.evaluatorId === persona?.sub);

  return (
    <div className="space-y-5">
      <PanelSection
        tender={tender}
        canAssign={isOfficerOrBoard}
        panel={panelQ.data?.items ?? []}
        loading={panelQ.loading}
        error={panelQ.error}
        onAssigned={() => { panelQ.reload(); onChanged(); }}
      />

      {isEvaluator && myMembership ? (
        <CoiSection tender={tender} membership={myMembership} onDeclared={panelQ.reload} />
      ) : null}

      {tender.status === 'CLOSED' ? (
        <UnsealSection tender={tender} panel={panelQ.data?.items ?? []} onUnsealed={onChanged} />
      ) : null}

      {tender.status === 'UNSEALED' && isOfficerOrBoard ? <StartEvaluationSection tender={tender} onStarted={onChanged} /> : null}

      {tender.status === 'EVALUATION' && isEvaluator && myMembership ? (
        <ScoringSection tender={tender} membership={myMembership} />
      ) : null}

      {canViewReport && ['EVALUATION', 'AWARD_PROPOSED', 'STANDSTILL', 'AWARDED', 'CONTRACTED'].includes(tender.status) ? (
        <ReportSection tender={tender} canFinalize={isBoard} onFinalized={onChanged} />
      ) : null}
    </div>
  );
}

function PanelSection({ tender, canAssign, panel, loading, error, onAssigned }: {
  tender: TenderNotice;
  canAssign: boolean;
  panel: EvaluationPanelMember[];
  loading: boolean;
  error: string | null;
  onAssigned: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [evaluatorIds, setEvaluatorIds] = useState('');
  const [threshold, setThreshold] = useState('2');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [justAssigned, setJustAssigned] = useState<PanelAssignResponse | null>(null);
  const canAssignNow = canAssign && panel.length === 0 && ['DRAFT', 'PUBLISHED'].includes(tender.status);

  const assign = async () => {
    setBusy(true);
    setFormError(null);
    try {
      const ids = evaluatorIds.split(',').map((s) => s.trim()).filter(Boolean);
      const res = await api.post<PanelAssignResponse>(`/api/v1/m11/tenders/${tender.id}/panel`, {
        evaluatorIds: ids,
        quorumThreshold: Number.parseInt(threshold, 10),
      });
      setJustAssigned(res);
      setShowForm(false);
      onAssigned();
    } catch (e) {
      setFormError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not assign the panel');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Evaluation panel</h2>
        {canAssignNow ? <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>+ Assign panel</button> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}

      {justAssigned ? (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="text-sm font-bold text-amber-800">Shamir shares — shown once, never stored</div>
          <p className="mt-1 text-xs text-amber-700">
            Deliver each share to its evaluator out of band right now. This portal cannot show them again.
          </p>
          <ul className="mt-2 space-y-1 font-mono text-[11px] text-amber-900">
            {justAssigned.shares.map((s) => (
              <li key={s.shareIndex} className="break-all rounded bg-white/70 px-2 py-1">#{s.shareIndex}: {s.shareToken}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {showForm ? (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
          {formError ? <ErrorNote message={formError} /> : null}
          <Field label="Evaluator SeyID subjects (comma-separated)" hint="e.g. acc_marieange_hoareau, acc_second_evaluator, acc_third_evaluator — 2 or more.">
            <input className="field-input" value={evaluatorIds} onChange={(e) => setEvaluatorIds(e.target.value)} />
          </Field>
          <Field label="Quorum threshold" className="!mb-0">
            <input className="field-input w-32" type="number" min="1" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          </Field>
          <button className="btn-primary !py-1 text-xs" onClick={assign} disabled={busy || evaluatorIds.split(',').filter((s) => s.trim()).length < 2}>
            {busy ? 'Assigning…' : 'Assign panel & mint seal key'}
          </button>
        </div>
      ) : null}

      {loading ? <Spinner /> : panel.length === 0 ? (
        <p className="text-xs text-slate-400">No panel assigned yet — required (and mints the sealed-bid key) before this tender can be published.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr><th className="table-head">Evaluator</th><th className="table-head text-center">Share #</th><th className="table-head">COI</th><th className="table-head">Status</th></tr></thead>
          <tbody>
            {panel.map((p) => (
              <tr key={p.id}>
                <td className="table-cell font-mono text-xs">{p.evaluatorId}</td>
                <td className="table-cell text-center tabular-nums">{p.shareIndex}</td>
                <td className="table-cell">{p.coiDeclared ? <StatusBadge status={p.coiConflict ? 'REJECTED' : 'APPROVED'} /> : <StatusBadge status="PENDING" />}</td>
                <td className="table-cell">{p.recused ? <StatusBadge status="SUSPENDED" /> : <StatusBadge status="ACTIVE" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CoiSection({ tender, membership, onDeclared }: { tender: TenderNotice; membership: EvaluationPanelMember; onDeclared: () => void }) {
  const [hasConflict, setHasConflict] = useState(false);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (membership.coiDeclared) {
    return <InfoNote message={`Your conflict-of-interest declaration is on file (${membership.coiConflict ? 'conflict declared — recused' : 'no conflict'}).`} />;
  }

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/coi`, { hasConflict, details: hasConflict ? details : undefined });
      onDeclared();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit — this action requires SIGNER (T3) tier.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Your conflict-of-interest declaration</h2>
      <p className="mb-3 text-xs text-slate-500">Required (T3/SIGNER) before you can score any bid on this tender. A declared conflict recuses you.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={!hasConflict} onChange={() => setHasConflict(false)} /> No conflict of interest</label>
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={hasConflict} onChange={() => setHasConflict(true)} /> I have a conflict of interest</label>
        {hasConflict ? <input className="field-input" placeholder="Describe the conflict…" value={details} onChange={(e) => setDetails(e.target.value)} /> : null}
        <button className="btn-primary" onClick={submit} disabled={busy || (hasConflict && details.length < 2)}>{busy ? 'Submitting…' : 'Submit declaration'}</button>
      </div>
    </div>
  );
}

function UnsealSection({ tender, panel, onUnsealed }: { tender: TenderNotice; panel: EvaluationPanelMember[]; onUnsealed: () => void }) {
  const [rows, setRows] = useState<Array<{ evaluatorId: string; shareToken: string }>>([{ evaluatorId: '', shareToken: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ bidsOpened: number } | null>(null);

  const setRow = (i: number, field: 'evaluatorId' | 'shareToken', value: string) => {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const shares = rows.filter((r) => r.evaluatorId && r.shareToken);
      const res = await api.post<{ bidsOpened: number }>(`/api/v1/m11/tenders/${tender.id}/unseal`, { shares });
      setResult(res);
      onUnsealed();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Unseal failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Quorum unseal ceremony</h2>
      <p className="mb-3 text-xs text-slate-500">
        Needs at least {tender.quorumThreshold} of {tender.quorumTotal} evaluator shares (of {panel.length} assigned) to reconstruct the private key and open every bid.
      </p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      {result ? (
        <InfoNote message={`Unsealed — ${result.bidsOpened} bid(s) decrypted. See the Bidding tab.`} />
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-2 gap-2">
              <input className="field-input" placeholder="Evaluator subject" value={row.evaluatorId} onChange={(e) => setRow(i, 'evaluatorId', e.target.value)} />
              <input className="field-input font-mono text-xs" placeholder="Share token (x.y)" value={row.shareToken} onChange={(e) => setRow(i, 'shareToken', e.target.value)} />
            </div>
          ))}
          <button className="btn-secondary !py-1 text-xs" onClick={() => setRows((r) => [...r, { evaluatorId: '', shareToken: '' }])}>+ Add share</button>
          <button className="btn-primary block" onClick={submit} disabled={busy}>{busy ? 'Unsealing…' : 'Unseal tender'}</button>
        </div>
      )}
    </div>
  );
}

function StartEvaluationSection({ tender, onStarted }: { tender: TenderNotice; onStarted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/start-evaluation`);
      onStarted();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not start evaluation');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card p-5">
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <button className="btn-primary" onClick={start} disabled={busy}>{busy ? 'Starting…' : 'Start evaluation phase'}</button>
    </div>
  );
}

function ScoringSection({ tender, membership }: { tender: TenderNotice; membership: EvaluationPanelMember }) {
  const bidsQ = useApiQuery<{ items: Bid[] }>(`/api/v1/m11/tenders/${tender.id}/bids`);
  const criteriaQ = useApiQuery<{ items: EvaluationCriterion[] }>(`/api/v1/m11/tenders/${tender.id}/criteria`);

  if (membership.recused) return <InfoNote message="You declared a conflict of interest and are recused from scoring this tender." />;
  if (!membership.coiDeclared) return <InfoNote message="Declare your conflict-of-interest status above before scoring." />;

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Score bids (blind — you never see another evaluator's scores)</h2>
      {bidsQ.error ? <ErrorNote message={bidsQ.error} /> : null}
      {criteriaQ.error ? <ErrorNote message={criteriaQ.error} /> : null}
      {bidsQ.loading || criteriaQ.loading ? (
        <Spinner />
      ) : (bidsQ.data?.items.length ?? 0) === 0 ? (
        <p className="text-xs text-slate-400">No bids to score.</p>
      ) : (
        <div className="space-y-4">
          {bidsQ.data!.items.map((bid) => (
            <BidScoreForm key={bid.id} tenderId={tender.id} bid={bid} criteria={criteriaQ.data?.items ?? []} />
          ))}
        </div>
      )}
    </div>
  );
}

function BidScoreForm({ tenderId, bid, criteria }: { tenderId: string; bid: Bid; criteria: EvaluationCriterion[] }) {
  const myScoresQ = useApiQuery<{ items: Array<{ criterionId: string; score: number | string }> }>(`/api/v1/m11/tenders/${tenderId}/bids/${bid.id}/scores/me`);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tenderId}/bids/${bid.id}/scores`, {
        scores: criteria.map((c) => ({ criterionId: c.id, score: Number.parseFloat(scores[c.id] ?? '0'), comments: comments || undefined })),
      });
      setSaved(true);
      myScoresQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not save scores');
    } finally {
      setBusy(false);
    }
  };

  const alreadyScored = (myScoresQ.data?.items.length ?? 0) === criteria.length && criteria.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-mono text-xs text-slate-500">bid {bid.id.slice(0, 10)}… · supplier {bid.supplierId.slice(0, 10)}…</span>
        {alreadyScored || saved ? <span className="text-xs font-semibold text-emerald-600">Scored</span> : null}
      </div>
      {error ? <ErrorNote message={error} /> : null}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {criteria.map((c) => (
          <Field key={c.id} label={`${c.code} (max ${num(c.maxScore)})`} className="!mb-0">
            <input
              className="field-input"
              type="number"
              min="0"
              max={num(c.maxScore) ?? undefined}
              defaultValue={myScoresQ.data?.items.find((s) => s.criterionId === c.id)?.score as string | number | undefined}
              onChange={(e) => setScores((s) => ({ ...s, [c.id]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
      <input className="field-input mt-2" placeholder="Comments (optional)" value={comments} onChange={(e) => setComments(e.target.value)} />
      <button className="btn-secondary mt-2 !py-1 text-xs" onClick={submit} disabled={busy}>{busy ? 'Saving…' : alreadyScored ? 'Update scores' : 'Save scores'}</button>
    </div>
  );
}

function ReportSection({ tender, canFinalize, onFinalized }: { tender: TenderNotice; canFinalize: boolean; onFinalized: () => void }) {
  const reportQ = useApiQuery<EvaluationReport>(`/api/v1/m11/tenders/${tender.id}/evaluation-report`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finalize = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/finalize-evaluation`);
      reportQ.reload();
      onFinalized();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not finalize');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Evaluation report</h2>
        {reportQ.data ? <StatusBadge status={reportQ.data.finalized ? 'FINAL' : 'PENDING'} /> : null}
      </div>
      {reportQ.error ? <ErrorNote message={reportQ.error} /> : null}
      {error ? <div className="mb-2"><ErrorNote message={error} /></div> : null}
      {reportQ.loading ? (
        <Spinner />
      ) : reportQ.data ? (
        <>
          {!reportQ.data.complete ? (
            <p className="mb-2 text-xs text-amber-600">{reportQ.data.missing.length} score(s) still missing across the panel.</p>
          ) : null}
          <table className="w-full text-sm">
            <thead><tr><th className="table-head">Rank</th><th className="table-head">Bid</th><th className="table-head">Supplier</th><th className="table-head text-right">Total score</th></tr></thead>
            <tbody>
              {reportQ.data.rankings.map((r, i) => (
                <tr key={r.bidId}><td className="table-cell tabular-nums">{i + 1}</td><td className="table-cell font-mono text-xs">{r.bidId.slice(0, 12)}…</td><td className="table-cell font-mono text-xs">{r.supplierId.slice(0, 12)}…</td><td className="table-cell text-right tabular-nums font-semibold">{r.totalScore}</td></tr>
              ))}
            </tbody>
          </table>
          {canFinalize && tender.status === 'EVALUATION' && !reportQ.data.finalized ? (
            <button className="btn-primary mt-3" onClick={finalize} disabled={busy || !reportQ.data.complete}>{busy ? 'Finalizing…' : 'Finalize evaluation'}</button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
